/**
 * Time Machine #114 Phase 6 — pure request-body builder for the Lambda's
 * `getEventLogForAttributeCell` method (introduced in queries-Lambda
 * `v05-09-2026-01-QRY01`).
 *
 * Drives the new TimeMachineAttributeHistoryModal: when the user clicks
 * an attribute pseudo-job in time-travel, this fetches every Attributes
 * EventLog entry whose before/after image matches BOTH `_kf_payrollNo`
 * and `date` — the full lifecycle (create / update / delete) of that
 * specific cell. Closes the helpdesk loop "I set an attribute on this
 * installer for this day, now it's gone, who changed it?"
 *
 * Inputs:
 *   payrollNo        — required string, formatted as the schedule grid
 *                      stores it (e.g. "3428.01" — already padded by the
 *                      frontend's dataFormatting_payrollFields).
 *   date             — required string in MM/DD/YYYY format (e.g. "11/6/2026"
 *                      — same shape stored by the schedule's
 *                      dataFormatting_dateFields → toLocaleDateString("en-us")).
 *   database         — "Warehouses" or "UAT". NOTE (#121): Lambda
 *                      `v05-09-2026-04-QRY01`+ overrides to "Warehouses"
 *                      for all Time Machine read methods regardless of
 *                      value passed.
 *   since (optional) — Date or ISO string lower bound (inclusive).
 *   until (optional) — Date or ISO string upper bound (inclusive).
 *   limit (optional) — int, default 200, max 1000.
 */

const VALID_DATABASES = ["Warehouses", "UAT"];
// Date format the frontend produces via toLocaleDateString("en-us"): single
// or double digit month and day, four-digit year, slash separators.
// Examples that should pass: "1/6/2026", "11/6/2026", "12/31/2026".
const DATE_RE = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

export function buildEventLogForAttributeCellRequestBody({
  payrollNo,
  date,
  database,
  since,
  until,
  limit,
} = {}) {
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildEventLogForAttributeCellRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (typeof payrollNo !== "string" || payrollNo.trim() === "") {
    throw new Error(
      `buildEventLogForAttributeCellRequestBody: payrollNo is required (got ${JSON.stringify(payrollNo)})`
    );
  }
  if (typeof date !== "string" || date.trim() === "") {
    throw new Error(
      `buildEventLogForAttributeCellRequestBody: date is required (got ${JSON.stringify(date)})`
    );
  }
  if (!DATE_RE.test(date)) {
    throw new Error(
      `buildEventLogForAttributeCellRequestBody: date must be M/D/YYYY or MM/DD/YYYY format (got ${JSON.stringify(date)})`
    );
  }

  const body = {
    method: "getEventLogForAttributeCell",
    database,
    // dispatcher artifact — the Lambda's createConnection requires `collection`,
    // but the handler picks the EventLog collection by name internally
    // regardless of this field. Use "AuditSnapshots" to match the convention
    // in buildEventLogByDocumentIdRequestBody and buildEventLogForCrewRequestBody
    // (the other Phase 5/6 EventLog builders).
    collection: "AuditSnapshots",
    payrollNo,
    date,
  };

  if (since != null) body.since = toIsoOrThrow(since, "since");
  if (until != null) body.until = toIsoOrThrow(until, "until");
  if (limit != null) {
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0) {
      throw new Error(
        `buildEventLogForAttributeCellRequestBody: limit must be a positive finite number (got ${JSON.stringify(limit)})`
      );
    }
    body.limit = Math.min(Math.floor(limit), 1000);
  }

  return body;
}

function toIsoOrThrow(value, label) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    throw new Error(
      `buildEventLogForAttributeCellRequestBody: ${label} must be a Date or ISO string (got ${JSON.stringify(value)})`
    );
  }
  return d.toISOString();
}

export const __testables__ = {
  VALID_DATABASES,
  DATE_RE,
};
